import type { ReactElement } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/hooks/use-auth'
import { PORTAL_HOME } from '@/lib/roles'
import { signInPathFor } from '@/lib/safe-next'

/**
 * ProtectedRoute — gates children behind an authenticated session.
 * - While loading: renders skeleton placeholder (no spinner per NFR-DS-2).
 * - If unauthenticated: redirects to /sign-in?next=<caminho+query> (Story 25.1).
 * - If the session belongs to a CUSTOMER: redirects to /portal (see below).
 * - If authenticated (Genesis): renders children.
 *
 * Story 1.6 ships the gate code; live OAuth verification is deferred
 * (same pattern as Story 1.5 AC5).
 *
 * F1 DO PORTAL DO CLIENTE — porque a regra do cliente mora AQUI e não em cada
 * tela: este componente embrulha, num sítio só, TODA a árvore autenticada
 * interna do `App.tsx`. Uma tela interna nova nasce automaticamente fechada ao
 * cliente, sem ninguém ter de se lembrar dela — que é o mesmo argumento com que
 * o §4 da spec recusa fechar rota a rota no servidor ("fail-open: um
 * esquecimento futuro vaza a base inteira").
 *
 * ⚠️ A árvore de `/portal` NÃO passa por aqui (usa o `PortalRoute`). Se algum
 * dia passar, o redirect abaixo entra em ciclo consigo próprio.
 */
export function ProtectedRoute({ children }: { children: ReactElement }) {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center p-8">
        <div className="w-full max-w-2xl space-y-4">
          <Skeleton className="h-12 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      </div>
    )
  }

  // O destino vai no URL do login, e não no estado do router (Story 25.1): o
  // `state.from` que vivia aqui morria num reload, e ninguém o lia. Quem decide
  // se o destino serve é o `safeNext`, do outro lado, depois de entrar.
  if (!user) {
    return <Navigate to={signInPathFor(location)} replace />
  }

  // Um usuário de cliente não existe fora de /portal/*. Isto é UX — o perímetro
  // real é o `requireAuth` da API a devolver 403 a `role='customer'` fora de
  // `/api/portal` —, mas sem ele o cliente veria a tela interna a montar,
  // esqueletos a girar e uma cascata de erros de rede antes de perceber que não
  // era ali. `replace` para o "voltar" não o trazer de novo para cá.
  if (user.role === 'customer') {
    return <Navigate to={PORTAL_HOME} replace />
  }

  return children
}
